import * as RiveModule from "@rive-app/react-canvas";

import type { FontAsset } from '@rive-app/react-canvas';
import type { IRiveAsset } from '../types';

/**
 * Asset loader function for Rive animations that handles font loading
 * @param asset - The asset to load
 * @param bytes - The asset bytes
 * @returns boolean indicating whether the asset should be loaded
 */
export const riveAssetLoader = () => {
  return (asset: IRiveAsset, bytes: Uint8Array) => {
    // If the asset has a CDN UUID or bytes, we don't need to load it
    if (asset.cdnUuid.length > 0 || bytes.length > 0) {
      return false;
    }

    // If the asset is a referenced font, we need to load it
    if (asset.isFont) {
      // Fetch the font file by name and extension.
      fetch(`/fonts/${asset.name}.ttf`).then(async (res) => {
        // decodeFont creates a Rive-specific Font object that `setFont()` takes
        // on the asset from assetLoader
        const font = await RiveModule.decodeFont(new Uint8Array(await res.arrayBuffer()));
        (asset as FontAsset).setFont(font);
    
        // Be sure to call unref to release any references.
        // This allows the engine to clean it up when it is not used by any more animations.
        font.unref();
      });
      return true;
    }
    
    return false;
  };
};

/**
 * Calculates the style object for Rive component based on layout configuration
 * @param flexibleLayout - Whether to use flexible layout
 * @param aspectRatio - The aspect ratio for normal layout
 * @param height - The height in pixels for flexible layout
 * @returns Style object with either aspectRatio or height property
 */
export const calculateRiveStyle = (
  flexibleLayout?: boolean,
  aspectRatio?: string,
  height?: string,
): { aspectRatio?: string; height?: string; } => {
  if (flexibleLayout) {
    return { height: `${height}px` };
  }
  return { aspectRatio: aspectRatio || '1' };
};
